<script setup>
import { computed, defineAsyncComponent, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import TheCheckoutTemplate from '@/components/checkout/template/TheCheckoutTemplate.vue'
import TheGuestCheckoutTemplate from '@/components/checkout/template/TheGuestCheckoutTemplate.vue'
import { translate } from '@/plugins/i18n.js'
import { useNewCheckoutStore } from '@/stores/newCheckout.js'
import { storeToRefs } from 'pinia'
import { useCartStore } from '../stores/cart.js'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)

const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)

const route = useRoute()
const router = useRouter()
const cart = useCartStore()
const checkoutStore = useNewCheckoutStore()

const { cartInstance } = storeToRefs(cart)

const showNewCheckout = computed(() => {
  return cartInstance.value?.canCheckout
})

const isGuestCheckout = computed(() => {
  return route.path.startsWith('/checkout/guest')
})

const checkCheckout = async () => {
  await cart.waitForUpdateFinished()

  if (!showNewCheckout.value) {
    const { default: MessageHelper } = await import(
      '@/services/helpers/MessageHelper'
    )
    MessageHelper.addErrorMessage('popover_cart_checkout_not_possible_yet')
    await router.replace({ name: 'cart' })
  }
}

const handleVisibilityChange = async () => {
  if (document.visibilityState === 'visible') {
    await cart.refreshCart()
    await checkCheckout()
    await cart.fetchDeliveryOptions()
  }
}

onMounted(async () => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  await checkCheckout()
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  checkoutStore.abortCheckout()
})
</script>

<template>
  <div data-elbglobals="page_type:checkout" id="content">
    <main id="checkout-template">
      <VContainer v-if="!showNewCheckout">
        <VRow>
          <VCol cols="12" class="text-center justify-center">
            <h2 class="my-5 px-3">
              {{ translate('checkout.loading') }}
            </h2>
            <VBtn
              color="white"
              icon
              rounded="50%"
              width="56"
              height="56"
              loading
            >
              <VIcon>custom:reset</VIcon>
            </VBtn>
          </VCol>
        </VRow>
      </VContainer>
      <TheGuestCheckoutTemplate v-else-if="isGuestCheckout" />
      <TheCheckoutTemplate v-else />
    </main>
    <TheFooter isReducedFooter />
  </div>
</template>

<style scoped>
main {
  min-height: 100vh;
}
</style>
